import {isEmpty} from '@shared/helpers/lang'
import {qs} from '@shared/helpers/queryString'

import {OrderSheetPageSet, OrderSheetPageSetType, PageSet, PageSetType} from '@web/core/constants'
import getApi, {createWrapper, getBaseUrl} from '@web/helpers/api'

import {
    BookingRewardResponse,
    ConfirmedBenefitCardRewardPointResponse,
    ConfirmedPurchaseRewardPointResponse,
    ConfirmedReviewRewardTotalPointResponse,
    EasyBookingFetchExpectedRewardPointResponse,
    ExpectedMembershipPointResponse,
    FamilyPayFetchExpectedPaymoneyRewardPointResponse,
    FetchBenefitAmountsV2Params,
    FetchBenefitAmountsV2Result,
    FetchExpectedMembershipPointParams,
    FetchExpectedRewardPointParams,
    GetBookingRewardRequest,
    GetReviewRewardInfoResponse,
    MallFetchExpectedRewardPointResponse,
    SellerFetchExpectedRewardPointResponse,
} from './interfaces'
import {RewardBenefitResponse} from './rewardBenefit'

const RewardPointApiUrlInfo = {
    [OrderSheetPageSet.MALL]: {
        /** 적립 예상 포인트 재조회 */
        expectedRewardPoint: '/orderSheet/mall/expectedRewardPoint',
    },
    [OrderSheetPageSet.EASYBOOKING]: {
        /** 적립 예상 포인트 재조회 */
        expectedRewardPoint: '/orderSheet/easyBooking/expectedRewardPoint',
    },
    [OrderSheetPageSet.SELLER]: {
        /** 적립 예상 포인트 재조회 */
        expectedRewardPoint: '/orderSheet/seller/expectedRewardPoint',
    },
    [OrderSheetPageSet.FAMILY_PAY]: {
        /** 적립 예상 포인트 재조회 */
        expectedRewardPoint: '/orderSheet/familyPay/expectedPaymoneyRewardPoint',
    },
    [OrderSheetPageSet.CLAIM]: {
        /** 클레임 비용결제는 포인트가 적립되지 않습니다. */
        expectedRewardPoint: '',
    },
}

/** 적립 예상 포인트 재조회 */
async function fetchExpectedRewardPoint(pageSet: OrderSheetPageSetType, params: FetchExpectedRewardPointParams) {
    const baseUrl = getBaseUrl(pageSet)
    const api = getApi(baseUrl)
    const url = RewardPointApiUrlInfo[pageSet].expectedRewardPoint

    /**
     * 적용된 쿠폰이 있을 경우만 걸러내는 검증함수
     */
    const validCouponInfo = (couponInfo: Record<string, string[]>) => {
        const result: Record<string, string[]> = {}
        Object.entries(couponInfo).forEach(([key, value]) => {
            if (!isEmpty(value)) {
                result[key] = value
            }
        })

        return result
    }
    const urlCreator = (rewardParam: FetchExpectedRewardPointParams) => {
        const {
            expectedRewardPointRequest: {
                selectedProductCouponIdsByOrderSheetItemId,
                /** C1에서 사용하지 않는 값들 */
                selectedProductDuplicateCouponIdByOrderSheetItemId,
                selectedImmediateDiscountPolicyNosByOrderSheetItemId,
                selectedStoreCouponIdsByMerchantNo,
                /** 그 외 필드 모두 payload로 사용 */
                ...rest
            },
            orderSheetId,
        } = rewardParam

        const couponInfo = validCouponInfo(selectedProductCouponIdsByOrderSheetItemId ?? {})
        return qs.stringifyUrl({
            url,
            query: {
                orderSheetId,
                ...rest,
                ...(isEmpty(couponInfo)
                    ? {}
                    : {
                          selectedProductCouponIdListsByOrderSheetItemId: JSON.stringify(couponInfo),
                      }),
            },
        })
    }
    if (pageSet === OrderSheetPageSet.MALL) {
        return createWrapper<MallFetchExpectedRewardPointResponse>(api.get(urlCreator(params)))
    }
    if (pageSet === OrderSheetPageSet.EASYBOOKING) {
        return createWrapper<EasyBookingFetchExpectedRewardPointResponse>(api.get(urlCreator(params)))
    }
    if (pageSet === OrderSheetPageSet.FAMILY_PAY) {
        return createWrapper<FamilyPayFetchExpectedPaymoneyRewardPointResponse>(
            api.get(
                qs.stringifyUrl({
                    url,
                    query: {
                        paymoneyUseAmount: params.expectedRewardPointRequest.paymoneyUseAmount,
                        orderNo: params.orderSheetId,
                    },
                }),
            ),
        )
    }
    // 가맹점, 예블카는 get 이므로, post 변경후 적용 필요
    return createWrapper<SellerFetchExpectedRewardPointResponse>(api.post(url, params))
}

/** 예상 적립금 조회 V2 */
export const fetchExpectedRewardPointV2 = async (
    pageSet: Maybe<PageSetType>,
    param: FetchBenefitAmountsV2Params,
): Promise<FetchBenefitAmountsV2Result> => {
    const baseUrl = (() => {
        switch (pageSet) {
            case PageSet.DICON_AUTO:
                return '/orderApi/paymentMethods'
            case PageSet.DICON_PAYMENT:
            default:
                return '/orderApi/payment/diconBuying'
        }
    })()
    const endPoint = (() => {
        switch (pageSet) {
            case PageSet.DICON_AUTO:
                return '/digitalContents/orderSheet/benefit/amounts'
            case PageSet.DICON_PAYMENT:
            default:
                return '/point/benefit/amounts/v2'
        }
    })()

    const {
        data: {result},
    } = await getApi(baseUrl).post(endPoint, param)

    return result
}

/** 상세 > 적립혜택 조회 API */
async function getRewardBenefit({
    orderNo,
}: {
    /** 주문결과 번호 */
    orderNo: string
}) {
    const api = getApi('/orderApi/orderSheet/detail')

    return createWrapper<RewardBenefitResponse>(
        api.get('/orderApi/orderSheet/detail/rewardBenefit', {
            params: {orderNo},
        }),
    )
}

/** 구매적립 확정 포인트 조회 */
async function fetchConfirmedPurchaseRewardPoint({
    productOrderNos,
    familyPaymentOrderNo,
    orderNo,
}: {
    productOrderNos: string[]
    familyPaymentOrderNo?: string
    orderNo?: string
}): Promise<ConfirmedPurchaseRewardPointResponse> {
    const baseUrl = getBaseUrl()
    const api = getApi(baseUrl)
    const url = qs.stringifyUrl({
        url: '/orderSheet/benefit/purchaseReward',
        query: {
            productOrderNos,
            familyPaymentOrderNo,
            orderNo,
        },
    })

    return createWrapper<ConfirmedPurchaseRewardPointResponse>(api.get(url))
}

/** 리뷰적립 확정 포인트 총합 조회 */
async function fetchConfirmedReviewRewardTotalPoint(orderNo: string): Promise<ConfirmedReviewRewardTotalPointResponse> {
    const baseUrl = getBaseUrl()
    const api = getApi(baseUrl)
    const url = qs.stringifyUrl({
        url: '/orderSheet/benefit/reviewRewardTotal',
        query: {
            orderNo,
        },
    })
    return createWrapper<ConfirmedReviewRewardTotalPointResponse>(api.get(url))
}

/**
 * 리뷰적립 정보 조회 > 스냅샷 / 이력 정보
 */
async function fetchReviewRewardInfo({
    productOrderNos,
    writtenReviewProductOrderNos,
}: {
    productOrderNos: string[]
    writtenReviewProductOrderNos?: string[]
}): Promise<GetReviewRewardInfoResponse> {
    const baseUrl = getBaseUrl()
    const api = getApi(baseUrl)
    const url = qs.stringifyUrl({
        url: '/orderSheet/benefit/reviewReward',
        query: {
            productOrderNos,
            writtenReviewProductOrderNos,
        },
    })

    return createWrapper<GetReviewRewardInfoResponse>(api.get(url))
}

/** 헤택카드 확정 포인트 조회 */
async function fetchConfirmedBenefitCardRewardTotalPoint(
    tid: string,
): Promise<ConfirmedBenefitCardRewardPointResponse> {
    const baseUrl = getBaseUrl()
    const api = getApi(baseUrl)
    const url = qs.stringifyUrl({
        url: '/orderSheet/universal/benefitCard',
        query: {
            tid,
        },
    })
    return createWrapper<ConfirmedBenefitCardRewardPointResponse>(api.get(url))
}

/** 멤버십 예상적립금 조회 */
async function fetchExpectedMembershipPoint(
    params: FetchExpectedMembershipPointParams,
): Promise<ExpectedMembershipPointResponse> {
    const baseUrl = getBaseUrl()
    const api = getApi(baseUrl)

    return createWrapper<ExpectedMembershipPointResponse>(api.post('/orderSheet/universal/expectedMembership', params))
}

/** 네이버예약 적립금 조회 */
async function fetchEasyBookingRewardPoint(params: GetBookingRewardRequest): Promise<BookingRewardResponse> {
    const baseUrl = getBaseUrl()
    const api = getApi(baseUrl)

    return createWrapper<BookingRewardResponse>(api.get('/orderSheet/easyBooking/reward', {params}))
}

export default {
    fetchExpectedRewardPoint,
    fetchExpectedRewardPointV2,
    getRewardBenefit,
    fetchConfirmedPurchaseRewardPoint,
    fetchConfirmedReviewRewardTotalPoint,
    fetchConfirmedBenefitCardRewardTotalPoint,
    fetchExpectedMembershipPoint,
    fetchReviewRewardInfo,
    fetchEasyBookingRewardPoint,
}
